import { createTRPCReact } from '@trpc/react-query';
import { httpLink, httpBatchLink, createTRPCProxyClient, splitLink } from '@trpc/client';
// ✅ tRPC类型自动同步：直接从后端导入AppRouter类型
// 注意：使用相对路径，因为 packages 现在在 frontend 下
import type { AppRouter } from '../../../../../backend/src/trpc/routers/_app';

/**
 * tRPC 客户端配置选项
 */
export interface TRPCConfig {
  /** API URL */
  apiUrl: string;
  /** 获取 token 的函数，默认从 localStorage 读取 */
  getToken?: () => string | null;
  /** 获取当前空间 ID（Hashid），用于请求头 x-space-id；不传则后端按默认规则选空间，多空间时可能选到无权限的空间 */
  getSpaceId?: () => string | null;
  /** 错误回调（可选） */
  onError?: (error: unknown) => void;
  /** 是否在开发环境打印日志 */
  enableDevLogs?: boolean;
}

/**
 * 创建 tRPC 客户端
 * 
 * @param config 配置选项
 * @returns tRPC React hooks 和客户端实例
 */
export function createTRPCClient(config: TRPCConfig) {
  const { apiUrl, getToken, getSpaceId, enableDevLogs = true } = config;

  // 验证 API URL
  if (!apiUrl || typeof apiUrl !== 'string' || apiUrl.trim() === '') {
    const errorMsg = 'tRPC API URL 未配置，请设置 VITE_API_URL 环境变量';
    console.error('[tRPC]', errorMsg);
    if (typeof window !== 'undefined') {
      throw new Error(errorMsg);
    }
  }

  // 开发环境下打印 API URL（便于调试）
  if (enableDevLogs && typeof window !== 'undefined') {
    const isDev = (import.meta as { env?: { DEV?: boolean } }).env?.DEV;
    if (isDev) {
    console.log('[tRPC] API URL:', apiUrl);
    }
  }

  const trpc = createTRPCReact<AppRouter>();

  // 通用的 fetch 和 headers 配置
  const commonConfig = {
    url: apiUrl!,
    // ✅ 统一认证中心：支持多端认证策略
    // - Web/H5 端：使用 Cookie（credentials: 'include'），支持跨子域 SSO
    // - 移动端：使用 Authorization Header（向后兼容）
    fetch: async (url: RequestInfo | URL, options?: RequestInit) => {
      // ✅ 开发环境下打印请求信息（便于调试）
      if (enableDevLogs && typeof window !== 'undefined') {
        const isDev = (import.meta as { env?: { DEV?: boolean } }).env?.DEV;
        if (isDev) {
          console.log('[tRPC] 发送请求:', {
            url: typeof url === 'string' ? url : url.toString(),
            method: options?.method || 'GET',
            credentials: 'include',
          });
        }
      }
      
      try {
        const response = await fetch(url, {
        ...options,
        credentials: 'include', // ✅ Web/H5 端：自动携带 Cookie
      });
        
        // ✅ 开发环境下打印响应信息（便于调试）
        if (enableDevLogs && typeof window !== 'undefined') {
          const isDev = (import.meta as { env?: { DEV?: boolean } }).env?.DEV;
          if (isDev) {
            console.log('[tRPC] 收到响应:', {
              status: response.status,
              statusText: response.statusText,
              ok: response.ok,
              url: response.url,
            });
          }
        }
        
        return response;
      } catch (error) {
        // ✅ 开发环境下打印错误信息（便于调试）
        if (enableDevLogs && typeof window !== 'undefined') {
          const isDev = (import.meta as { env?: { DEV?: boolean } }).env?.DEV;
          if (isDev) {
            console.error('[tRPC] 请求失败:', {
              url: typeof url === 'string' ? url : url.toString(),
              error: error instanceof Error ? error.message : String(error),
              errorType: error instanceof TypeError ? 'TypeError (网络错误)' : '其他错误',
            });
          }
        }
        throw error;
      }
    },
    async headers() {
      // ✅ 移动端：继续支持从 localStorage 读取 token（向后兼容）
      // Web/H5 端会优先使用 Cookie，但也可以用 Header 作为后备
      const token = getToken
        ? getToken()
        : typeof window !== 'undefined'
        ? localStorage.getItem('token')
        : null;

      if (enableDevLogs && typeof window !== 'undefined' && token) {
        const isDev = (import.meta as { env?: { DEV?: boolean } }).env?.DEV;
        if (isDev) {
        console.log('[tRPC] 使用 Authorization Header 进行认证（移动端或后备）');
        }
      }

      const headers: Record<string, string> = {
        authorization: token ? `Bearer ${token}` : '',
      };
      const spaceId = getSpaceId?.() ?? null;
      if (spaceId) {
        headers['x-space-id'] = spaceId;
      }
      return headers;
    },
  };

  // React hooks 客户端（用于组件中使用）
  const trpcClient = trpc.createClient({
    links: [
      // ✅ 关键修复：mutation 使用非批量请求，以正确支持 Cookie
      // query 仍然使用批量请求优化性能
      splitLink({
        condition: (op) => op.type === 'mutation',
        // mutation: 使用 httpLink（非批量），确保 Cookie 正确设置
        true: httpLink(commonConfig),
        // query: 使用 httpBatchLink（批量），优化性能
        false: httpBatchLink(commonConfig),
      }),
    ],
  });

  // ✅ 创建代理客户端（用于直接调用，不使用 React hooks）
  // 这个客户端可以在非 React 组件中使用，比如工具函数中
  const trpcProxyClient = createTRPCProxyClient<AppRouter>({
    links: [
      // ✅ 代理客户端也使用 splitLink 确保 Cookie 正确
      splitLink({
        condition: (op) => op.type === 'mutation',
        // mutation: 使用 httpLink（非批量），确保 Cookie 正确设置
        true: httpLink(commonConfig),
        // query: 使用 httpBatchLink（批量），优化性能
        false: httpBatchLink(commonConfig),
      }),
    ],
  });

  return { trpc, trpcClient, trpcProxyClient };
}

